export interface PooshinoInitOptions {
  apiKey: string;
  baseUrl?: string;
}

export interface OpenTryOnOptions {
  productId: string;
  productImageUrl: string;
  productName?: string;
  /** Product family hint: glass | tops | footwear | … */
  type?: string;
  /** Free-form size text for any product (eyewear mm labels, clothing size, …). */
  size?: string;
}

export interface TryOnResult {
  jobId: string;
  resultUrl: string;
}

type MessageType = 'POOSHINO_INIT' | 'POOSHINO_READY' | 'POOSHINO_RESULT' | 'POOSHINO_ERROR' | 'POOSHINO_CLOSE';

interface PooshinoMessage {
  type: MessageType;
  payload?: unknown;
}

const scriptOrigin = (() => {
  try {
    const current = document.currentScript as HTMLScriptElement | null;
    if (current?.src) return new URL(current.src).origin;
    const scripts = document.querySelectorAll(
      'script[src*="sdk.js"], script[src*="sdk-eyewear.js"]',
    );
    const last = scripts[scripts.length - 1] as HTMLScriptElement | undefined;
    if (last?.src) return new URL(last.src).origin;
  } catch {
    return '';
  }
  return '';
})();

class PooshinoClient {
  private apiKey = '';
  private baseUrl = '';
  private overlay: HTMLDivElement | null = null;
  private iframe: HTMLIFrameElement | null = null;
  private resultHandler: ((result: TryOnResult) => void) | null = null;
  private errorHandler: ((error: Error) => void) | null = null;
  private listening = false;
  private clicksBound = false;
  private widgetEnabled = true;

  init(options: PooshinoInitOptions): void {
    if (!options.apiKey) {
      throw new Error('Pooshino.init requires apiKey');
    }
    this.apiKey = options.apiKey;
    this.baseUrl = (options.baseUrl ?? this.inferBaseUrl()).replace(/\/$/, '');
    if (!this.listening) {
      window.addEventListener('message', (event) => this.onMessage(event));
      this.listening = true;
    }
    this.bindTryOnButtons();
    void this.loadWidgetConfig();
  }

  openTryOn(options: OpenTryOnOptions): Promise<TryOnResult> {
    if (!this.apiKey) {
      throw new Error('Call Pooshino.init before openTryOn');
    }
    if (!this.widgetEnabled) {
      return Promise.reject(new Error('Try-on button is disabled for this store'));
    }
    return new Promise((resolve, reject) => {
      const timeout = window.setTimeout(() => {
        this.finish();
        reject(new Error('Try-on timed out'));
      }, 180000);
      this.resultHandler = (result) => {
        window.clearTimeout(timeout);
        this.finish();
        resolve(result);
      };
      this.errorHandler = (error) => {
        window.clearTimeout(timeout);
        this.finish();
        reject(error);
      };
      this.mount(options);
    });
  }

  private finish(): void {
    this.resultHandler = null;
    this.errorHandler = null;
  }

  close(): void {
    this.overlay?.remove();
    this.overlay = null;
    this.iframe = null;
    document.body.style.overflow = '';
    document.documentElement.style.overflow = '';
  }

  private inferBaseUrl(): string {
    if (scriptOrigin) return scriptOrigin;
    const scripts = document.querySelectorAll(
      'script[src*="sdk.js"], script[src*="sdk-eyewear.js"]',
    );
    const last = scripts[scripts.length - 1] as HTMLScriptElement | undefined;
    if (last?.src) return new URL(last.src).origin;
    return window.location.origin;
  }

  private bindTryOnButtons(): void {
    if (this.clicksBound) return;
    this.clicksBound = true;
    document.addEventListener('click', (event) => {
      const target = (event.target as HTMLElement | null)?.closest?.('[data-pooshino-tryon]') as HTMLElement | null;
      if (!target) return;
      if (!this.widgetEnabled) {
        event.preventDefault();
        return;
      }
      event.preventDefault();
      const productId = target.getAttribute('data-product-id') || '';
      const productImageUrl = target.getAttribute('data-product-image') || '';
      const productName = target.getAttribute('data-product-name') || undefined;
      const type =
        target.getAttribute('data-type') ||
        target.getAttribute('data-pooshino-type') ||
        undefined;
      const size =
        target.getAttribute('data-size') ||
        target.getAttribute('data-pooshino-size') ||
        undefined;
      if (!productId || !productImageUrl) {
        console.error('Pooshino: data-product-id and data-product-image are required');
        return;
      }
      void this.openTryOn({ productId, productImageUrl, productName, type, size }).catch(() => undefined);
    });
  }

  private async loadWidgetConfig(): Promise<void> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/try-on/widget-config`, {
        headers: {
          'X-Api-Key': this.apiKey,
          'X-Parent-Origin': window.location.origin,
        },
      });
      if (!response.ok) {
        return;
      }
      const json = (await response.json().catch(() => ({}))) as {
        data?: { tryOnButtonEnabled?: boolean };
        tryOnButtonEnabled?: boolean;
        success?: boolean;
        error?: { code?: string };
      };
      if (json.success === false || json.error?.code) {
        return;
      }
      const data = json.data ?? json;
      this.widgetEnabled = data.tryOnButtonEnabled !== false;
      if (!this.widgetEnabled) {
        this.hideTryOnButtons();
      }
    } catch {
      // Keep the merchant button visible if the config request fails.
    }
  }

  private disableWidget(): void {
    this.widgetEnabled = false;
    this.hideTryOnButtons();
  }

  private hideTryOnButtons(): void {
    document.querySelectorAll('[data-pooshino-tryon]').forEach((node) => {
      const element = node as HTMLElement;
      const wrap = element.closest('.pooshino-tryon-wrap');
      if (wrap instanceof HTMLElement) {
        wrap.style.display = 'none';
        return;
      }
      element.style.display = 'none';
    });
  }

  private mount(options: OpenTryOnOptions): void {
    this.close();
    const overlay = document.createElement('div');
    overlay.setAttribute('data-pooshino', 'overlay');
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    const mobile = window.matchMedia('(max-width: 720px)').matches;
    overlay.style.cssText = mobile
      ? 'position:fixed;inset:0;background:rgba(15,23,42,.62);z-index:2147483646;display:flex;align-items:stretch;justify-content:stretch;padding:0;overflow:hidden;'
      : 'position:fixed;inset:0;background:rgba(15,23,42,.62);z-index:2147483646;display:flex;align-items:center;justify-content:center;padding:16px;overflow:hidden;';
    const frame = document.createElement('iframe');
    frame.setAttribute('allow', 'camera; microphone');
    frame.setAttribute('title', 'Pooshino Virtual Try-On');
    frame.style.cssText = mobile
      ? 'width:100%;height:100%;height:100dvh;border:0;border-radius:0;background:#fff;overflow:hidden;'
      : 'width:min(860px,calc(100vw - 32px));height:min(820px,calc(100svh - 32px));max-height:calc(100svh - 32px);border:0;border-radius:16px;background:#fff;box-shadow:0 8px 24px rgba(15,23,42,.12);overflow:hidden;';
    overlay.appendChild(frame);
    overlay.addEventListener('click', (event) => {
      event.stopPropagation();
    });
    frame.addEventListener('load', () => {
      this.post({
        type: 'POOSHINO_INIT',
        payload: {
          apiKey: this.apiKey,
          productId: options.productId,
          productImageUrl: options.productImageUrl,
          productName: options.productName,
          type: options.type,
          size: options.size,
          parentOrigin: window.location.origin,
        },
      });
    });
    document.body.appendChild(overlay);
    this.overlay = overlay;
    this.iframe = frame;
    frame.src = `${this.baseUrl}/design/try-on?embed=1`;
  }

  private post(message: PooshinoMessage): void {
    this.iframe?.contentWindow?.postMessage(message, this.baseUrl);
  }

  private onMessage(event: MessageEvent<PooshinoMessage>): void {
    if (event.origin !== this.baseUrl) {
      return;
    }
    const data = event.data;
    if (!data || typeof data !== 'object') {
      return;
    }
    if (data.type === 'POOSHINO_RESULT' && this.resultHandler) {
      this.resultHandler(data.payload as TryOnResult);
      return;
    }
    if (data.type === 'POOSHINO_ERROR' && this.errorHandler) {
      const payload = (data.payload || {}) as { message?: string };
      this.errorHandler(new Error(payload.message || 'Try-on failed'));
      return;
    }
    if (data.type === 'POOSHINO_CLOSE') {
      this.errorHandler?.(new Error('Try-on cancelled'));
      this.close();
    }
  }
}

const client = new PooshinoClient();

export const init = client.init.bind(client);
export const openTryOn = client.openTryOn.bind(client);
export const close = client.close.bind(client);

export default client;
